<template>
  <div class="delete-intent">
    <n-modal @cancel="cancel">
      <template v-slot:title>
        <p class="modal-card-title">Delete intent</p>
      </template>
      <template v-slot:body>
        <p v-if="(!intent.references || intent.references.length === 0) && !isUnknownIntent()">
          Are you sure you want to delete <strong>{{ intent.title }}</strong
          >?
        </p>
        <p v-else-if="isUnknownIntent()">
          The <strong>Unknown</strong> intent is a system-defined intent and cannot be deleted.
        </p>
        <div v-else>
          <p>You are currently unable to delete this intent because it is used in the following:</p>
          <div v-if="!hasLoadedReferences()">
            <n-loading-spinner />
          </div>
          <div v-else>
            <div v-if="answerReferences.length > 0" class="delete-intent__answer-references">
              <h2 class="title is-6">FAQs</h2>
              <ul>
                <li v-for="answerReference in answerReferences" :key="answerReference.id">
                  <router-link :to="{ name: 'bots-answer-editor', params: { id: answerReference.id } }">{{
                    getAnswerName(answerReference)
                  }}</router-link>
                </li>
              </ul>
            </div>
            <div v-if="flowReferences.length > 0" class="delete-intent__flow-references">
              <h2 class="title is-6">Flows</h2>
              <ul>
                <li v-for="flowReference in flowReferences" :key="flowReference.id">
                  <router-link :to="{ name: 'bots-flow-designer', params: { flowId: flowReference.id } }">{{
                    getFlowName(flowReference)
                  }}</router-link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:buttons>
        <n-button-group>
          <n-button
            id="delete-confirm-button"
            v-if="!isConfirmHidden()"
            variant="primary"
            :loading="deleteInProgress"
            @click="confirmDelete"
            >Confirm</n-button
          >
          <n-button @click="cancel" variant="primary--outline">Cancel</n-button>
        </n-button-group>
      </template>
    </n-modal>
  </div>
</template>
<script lang="ts" setup>
import Toast from '@/shared/services/toast';
import { LOAD_FLOWS } from '@/store/bot-designer/actionTypes';
import { LOAD_ANSWERS } from '@/store/answers/actionTypes';
import { DELETE_INTENT } from '@/store/intents/actionTypes';
import { AnswerListItemViewModel } from '../../models/answers/answerListItemViewModel';
import { FlowListItemViewModel } from '../../models/flowListItemViewModel';
import { IntentListItemViewModel, IntentReferenceViewModel } from '../../models/intents/intentListItemViewModel';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import { computed, onMounted, PropType, ref } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  intent: {
    type: Object as PropType<IntentListItemViewModel>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'deleted'): void;
  (e: 'cancel'): void;
  (e: 'failedToDelete'): void;
}>();

const store = useStore();
const loadFlows = (doLoad = false): Promise<FlowListItemViewModel[]> => store.dispatch(LOAD_FLOWS, doLoad);
const loadAnswers = (doLoad = false): Promise<AnswerListItemViewModel[]> => store.dispatch(LOAD_ANSWERS, doLoad);
const deleteIntent = (intentId: string): Promise<void> => store.dispatch(DELETE_INTENT, intentId);

const flows = ref<FlowListItemViewModel[]>([]);
const answers = ref<AnswerListItemViewModel[]>([]);
const deleteInProgress = ref(false);
const loadedFlows = ref(false);
const loadedAnswers = ref(false);

const flowReferences = computed<IntentReferenceViewModel[]>(() =>
  props.intent.references?.filter((r) => r.type === 'flow'),
);
const answerReferences = computed<IntentReferenceViewModel[]>(() =>
  props.intent.references?.filter((r) => r.type === 'answer'),
);

onMounted(async () => {
  if (flowReferences.value && flowReferences.value.length > 0) {
    await getFlows();
  }
  if (answerReferences.value && answerReferences.value.length > 0) {
    await getAnswers();
  }
});

async function getFlows(): Promise<void> {
  flows.value = await loadFlows();
  loadedFlows.value = true;
}

async function getAnswers(): Promise<void> {
  answers.value = await loadAnswers();
  loadedAnswers.value = true;
}

function cancel(): void {
  emit('cancel');
}

function getAnswerName(intentReference: IntentReferenceViewModel): string {
  const answer = answers.value?.find((a) => a.id === intentReference.id);
  return answer ? answer.title : '';
}

function getFlowName(intentReference: IntentReferenceViewModel): string {
  const flow = flows.value?.find((f) => f.id === intentReference.id);
  return flow ? flow.name : '';
}

async function confirmDelete(): Promise<void> {
  deleteInProgress.value = true;
  try {
    await deleteIntent(props.intent.id);
    Toast.showSuccess(`Successfully deleted intent ${props.intent.title}`);
    emit('deleted');
  } catch (e: any) {
    Toast.showError(`Failed to delete intent: ${e.message}`);
    emit('failedToDelete');
  } finally {
    deleteInProgress.value = false;
  }
}

function isUnknownIntent(): boolean {
  return props.intent.id === 'None';
}

function isConfirmHidden(): boolean {
  return isUnknownIntent() || (props.intent.references && props.intent.references.length > 0);
}

function hasLoadedReferences(): boolean {
  return (
    (answerReferences.value.length === 0 || loadedAnswers.value) &&
    (flowReferences.value.length === 0 || loadedFlows.value)
  );
}
</script>

<style lang="scss" scoped>
li::before {
  content: '•';
  color: $color-netcall-salmon;
  margin-right: 0.5rem;
}
h2.title {
  margin-bottom: 0;
}
.delete-intent__flow-references,
.delete-intent__answer-references {
  margin-top: 1rem;
}
.sk-folding-cube {
  margin: 2rem auto;
}
</style>
